import { useMemo, useRef, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { MetaTags } from '@components/MetaTags';
import HeroSection from '@composites/HeroSection';
import MiddleTextWithUnderline from '@composites/MiddleTextWithUnderline';
import PosterWithDividerText from '@composites/IllustratedTextContent';
import { useMobileWidth } from '@utils/hooks';

import { getEventDetails } from '@store/slices/eventSlice';
import { getImageSrc } from '@utils/fn';
import { Page, Section } from './styles';
import UnderLine from '@components/Underline';
import Button from '@components/Button';
import { useTheme } from 'styled-components';
import { Column } from '@components/LinearLayout';
import FadeInOnScroll from '@components/Transitions/FadeInScroll';
import TilesOnScroll from '@components/Transitions/TilesOnScroll';

import {
  PartnersPartnerWithUsSectionWrapper as FeaturesWrapper,
  Title as FeaturesTitle,
  Description as FeaturesDescription,
  CardsWrapper as FeaturesCardsWrapper,
  Card as FeaturesCard,
  CardTitle as FeaturesCardTitle,
  CardContent as FeaturesCardContent,
} from '@pages/Partners/components/PartnersPartnerWithUsSection/styles';
import { TFunction } from 'i18next';
import { MULTI_TOUCHPOINT_ITEMS } from '@utils/constants';
import { GridSpan, SectionGrid } from '@pages/EventHome/styles';
import EventTicketsSection from '@pages/EventHome/components/EventTicketsSection';
import IllustratedText from '@composites/IllustratedText';

const MultiTouchpointNetworkingPageEvent = () => {
  const { t, i18n } = useTranslation();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const isMobile = useMobileWidth();
  const location = useLocation();
  const sections = useMemo(
    () => MULTI_TOUCHPOINT_ITEMS(t, sluggedEvent?.name),
    [t, sluggedEvent?.name]
  );
  const topRef = useRef<HTMLDivElement>(null);
  const langIsDe = (sluggedEvent?.language || i18n.language || 'en')
    .toLowerCase()
    .startsWith('de');

  const metaTitle = `${sluggedEvent?.name} – ${t('mtpn-hero-title')}`;

  useEffect(() => {
    if (!location.hash) return;
    const id = decodeURIComponent(location.hash.slice(1));

    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return true;
      }
      return false;
    };

    if (!tryScroll()) {
      requestAnimationFrame(() => {
        if (!tryScroll()) {
          setTimeout(tryScroll, 300);
        }
      });
    }
  }, [location.hash]);

  return (
    <Page ref={topRef}>
      <MetaTags
        title={metaTitle}
        description={t('mtpn-hero-description')}
        keywords={`${sluggedEvent?.slug}, multi touchpoint networking`}
        image={getImageSrc(sluggedEvent?.design?.eventLogoPosSvg)}
        url={window.location.href}
        twitterCardType="summary_large_image"
        type="website"
      />
      <SectionGrid>
        <GridSpan span={12}>
          <HeroSection
            backgroundImage="https://media.we-conect.com/images/website/speaker_hero_bg.png"
            darkOverlay
            height={isMobile ? '94rem' : '84rem'}
            title={t('mtpn-hero-title')}
            description={t('mtpn-hero-description', {
              eventName: sluggedEvent?.name || '',
            })}
            buttonText={t('book-now')}
            buttonURL={
              sluggedEvent?.slug
                ? `/events/${sluggedEvent.slug}/${langIsDe ? 'jetzt-buchen' : 'book-now'}`
                : '#intro'
            }
            openInNewTab={false}
          />
        </GridSpan>

        <GridSpan span={12}>
          {sections.map((s, idx) => {
            const orientation = isMobile
              ? 'right'
              : idx % 2 === 0
                ? 'left'
                : 'right';

            return (
              <Section id={s.id} key={s.id}>
                <IllustratedText
                  images={[s.img]}
                  orientation={orientation}
                  content={
                    <MiddleTextWithUnderline
                      isSpecialPage
                      align="left"
                      title={s.title}
                      underlineColor={sluggedEvent?.design?.themeColor}
                      paragraphs={[{ content: s.text }]}
                    />
                  }
                />
              </Section>
            );
          })}
        </GridSpan>

        <GridSpan span={12}>
          <EventTicketsSection />
        </GridSpan>

        {/*
      <Section id="intro">
        <MiddleTextWithUnderline
          isSpecialPage
          title={t('mtpn-text-title')}
          underlineColor={sluggedEvent?.design?.themeColor}
          paragraphs={[
            { content: t('mtpn-text-body-1') },
            { content: t('mtpn-text-body-2') },
            { content: t('mtpn-text-body-3') },
          ]}
        />
      </Section>
      */}

        {/*
      <FeaturesWrapper>
        <FadeInOnScroll threshold={0.8}>
          <Column $justifyContent="center" $alignItems="center" $gap="1.7rem">
            <FeaturesTitle
              style={!isMobile ? { whiteSpace: 'pre-line' } : undefined}
            >
              {t('mtpn-features.headline')}
            </FeaturesTitle>
            <UnderLine
              width="16.3rem"
              color={sluggedEvent?.design?.themeColor}
            />
            <FeaturesDescription
              style={!isMobile ? { padding: '2rem 30rem' } : undefined}
            >
              {t('mtpn-features.intro')}
            </FeaturesDescription>
          </Column>
        </FadeInOnScroll>

        <Column $gap="4.4rem" $justifyContent="center" $alignItems="center">
          <FeaturesCardsWrapper>
            <TilesOnScroll threshold={0.1} staggerDelay={80}>
              {[
                'event-planning',
                'vod',
                'matchmaking',
                'exposure',
                'analytics',
              ].map(key => (
                <article key={key}>
                  <FeaturesCard>
                    <FeaturesCardTitle color={sluggedEvent?.design?.themeColor}>
                      {t(`mtpn-features.cards.${key}.title`)}
                    </FeaturesCardTitle>
                    <FeaturesCardContent>
                      {t(`mtpn-features.cards.${key}.body`)}
                    </FeaturesCardContent>
                  </FeaturesCard>
                </article>
              ))}
            </TilesOnScroll>
          </FeaturesCardsWrapper>

          <Button
            title={t('book-now')}
            backgroundColor={sluggedEvent?.design?.themeColor}
            hoverBackgroundColor={theme.color.white}
            textColor={theme.color.white}
            hoverTextColor={sluggedEvent?.design?.themeColor}
            borderRadius={theme.borderRadius.radius10}
            fontSize={theme.fontSize.size20}
            lineHeight={theme.lineHeight.height150}
            padding={isMobile ? '1.5rem 2.5rem' : '1.1rem 4.9rem'}
            onClick={() => {
              const path = langIsDe ? 'jetzt-buchen' : 'book-now';
              if (!sluggedEvent?.slug) return;
              window.location.href = `${window.location.origin}/events/${sluggedEvent.slug}/${path}`;
            }}
          />
        </Column>
      </FeaturesWrapper>
      */}
      </SectionGrid>
    </Page>
  );
};

export default MultiTouchpointNetworkingPageEvent;
